import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Shield, ShieldAlert, ShieldCheck, ArrowRight, RefreshCcw, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ASSESSMENT_QUESTIONS, BUSINESS_NAME } from '@/src/constants';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import SEO from '@/src/components/SEO';

export default function Assessment() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [showResults, setShowResults] = useState(false);

  const handleAnswer = (questionId: string, score: number) => {
    const newAnswers = { ...answers, [questionId]: score };
    setAnswers(newAnswers);

    if (currentStep < ASSESSMENT_QUESTIONS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowResults(true);
    }
  };

  const resetAssessment = () => {
    setCurrentStep(0);
    setAnswers({});
    setShowResults(false);
  };

  const calculateScore = () => {
    const totalScore: number = (Object.values(answers) as number[]).reduce((acc: number, curr: number) => acc + curr, 0);
    const maxScore: number = ASSESSMENT_QUESTIONS.length * 10;
    const percentage: number = (totalScore / maxScore) * 100;
    return { totalScore, maxScore, percentage };
  };

  const getRiskLevel = (percentage: number) => {
    if (percentage >= 80) return { label: "Low Risk", color: "text-green-500", icon: ShieldCheck, bg: "bg-green-500/10" };
    if (percentage >= 50) return { label: "Medium Risk", color: "text-yellow-500", icon: Info, bg: "bg-yellow-500/10" };
    return { label: "High Risk", color: "text-red-500", icon: ShieldAlert, bg: "bg-red-500/10" };
  };

  const { percentage } = calculateScore();
  const risk = getRiskLevel(percentage);

  return (
    <div className="flex flex-col min-h-[80vh]">
      <SEO 
        title="Security Posture Audit – Interactive Assessment | AGRANDINTL"
        description="Evaluate your digital defenses with AGRANDINTL's proprietary security posture assessment tool. Get instant feedback on your business's cybersecurity gaps."
        keywords="security posture audit, cybersecurity assessment, risk tool, digital defense evaluation, AGRANDINTL assessment"
      />
      {/* Header */}
      <section className="py-32 bg-secondary/5 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-accent/5 opacity-10" />
        <div className="container mx-auto px-4 md:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto space-y-6"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent text-[10px] font-bold uppercase tracking-[0.3em]">
              Security Intelligence Engine
            </div>
            <h1 className="text-4xl md:text-6xl font-display font-bold tracking-tight leading-tight">
              Security <span className="text-accent">Posture Audit</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground font-normal max-w-2xl mx-auto leading-relaxed">
              Our proprietary assessment framework evaluates your digital defenses against modern threat vectors.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 flex-grow">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <AnimatePresence mode="wait">
            {!showResults ? (
              <motion.div
                key="question"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-xs font-bold font-display uppercase tracking-widest text-muted-foreground">
                    <span>Step {currentStep + 1} of {ASSESSMENT_QUESTIONS.length}</span>
                    <span>{Math.round(((currentStep + 1) / ASSESSMENT_QUESTIONS.length) * 100)}% Complete</span>
                  </div>
                  <div className="h-1.5 w-full bg-secondary rounded-full overflow-hidden">
                    <motion.div 
                      className="h-full bg-accent"
                      initial={{ width: 0 }}
                      animate={{ width: `${((currentStep + 1) / ASSESSMENT_QUESTIONS.length) * 100}%` }}
                    />
                  </div>
                </div>

                <Card className="glass-card border-white/5 overflow-hidden">
                  <CardContent className="p-6 md:p-10 space-y-6">
                    <h2 className="text-xl md:text-2xl font-display font-bold leading-tight">
                      {ASSESSMENT_QUESTIONS[currentStep].question}
                    </h2>
                    <div className="grid grid-cols-1 gap-3">
                      {ASSESSMENT_QUESTIONS[currentStep].options.map((option, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleAnswer(ASSESSMENT_QUESTIONS[currentStep].id, option.score)}
                          className="group relative flex items-center justify-between p-5 rounded-xl border border-white/10 bg-white/5 hover:bg-accent/5 hover:border-accent/30 transition-all text-left"
                        >
                          <span className="text-base font-medium group-hover:text-accent transition-colors">
                            {option.label}
                          </span>
                          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-accent group-hover:translate-x-1 transition-all" />
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ) : (
              <motion.div
                key="results"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-12"
              >
                {/* Score Overview */}
                <div className="text-center space-y-4">
                  <div className={cn("inline-flex items-center gap-2 px-4 py-2 rounded-full font-display font-bold text-lg uppercase tracking-widest", risk.bg, risk.color)}>
                    <risk.icon className="w-5 h-5" />
                    {risk.label}
                  </div>
                  <h2 className="text-3xl md:text-5xl font-display font-bold">
                    Your Security Score: <span className="text-accent">{Math.round(percentage)}%</span>
                  </h2>
                  <p className="text-muted-foreground max-w-xl mx-auto text-base">
                    Based on your answers, here is a summary of your current security posture and immediate actions you should take.
                  </p>
                </div>

                {/* Recommendations */}
                <div className="grid grid-cols-1 gap-6">
                  {ASSESSMENT_QUESTIONS.map((q) => {
                    const userScore = answers[q.id];
                    const isGood = userScore === 10;
                    return (
                      <Card key={q.id} className={cn("glass-card border-white/5", !isGood && "border-red-500/20")}>
                        <CardContent className="p-6 flex flex-col md:flex-row gap-6 items-start">
                          <div className={cn("shrink-0 w-12 h-12 rounded-full flex items-center justify-center", isGood ? "bg-green-500/10 text-green-500" : "bg-red-500/10 text-red-500")}>
                            {isGood ? <CheckCircle2 className="w-6 h-6" /> : <AlertTriangle className="w-6 h-6" />}
                          </div>
                          <div className="space-y-2">
                            <h3 className="font-display font-bold text-lg">{q.question}</h3>
                            <p className="text-muted-foreground text-sm leading-relaxed">
                              {isGood ? "Great job! Your practice meets industry standards." : q.recommendation}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Next Steps */}
                <div className="glass-card rounded-3xl p-8 md:p-10 border-accent/10 bg-accent/5 text-center space-y-6">
                  <div className="space-y-3">
                    <h3 className="text-2xl font-display font-bold">Ready for a Deep Dive?</h3>
                    <p className="text-muted-foreground text-sm max-w-xl mx-auto">
                      This assessment is just the beginning. Our experts can perform a comprehensive audit to uncover hidden risks and build a robust defense strategy for your business.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Button asChild size="lg" variant="accent" className="w-full sm:w-auto font-bold h-12 px-8">
                      <Link to="/contact">Book Free Consultation</Link>
                    </Button>
                    <Button asChild variant="outline" className="w-full sm:w-auto h-12 px-8 border-white/10">
                      <Link to="/pricing">View Packages</Link>
                    </Button>
                    <Button variant="outline" onClick={resetAssessment} className="w-full sm:w-auto h-12 px-8 border-white/10">
                      <RefreshCcw className="mr-2 w-4 h-4" /> Retake
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
}
